import { REACT_APP_WEBSOCKET_URL } from 'src/utils/config';

type WSData = any;

class WebSocketService {
  private socket: WebSocket | null = null;
  private listeners: Array<(data: WSData) => void> = [];
  private userId: string | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private hbInterval: ReturnType<typeof setInterval> | null = null;
  private hbWatchdog: ReturnType<typeof setTimeout> | null = null;
  private isConnecting = false;
  private lastPongAt = 0;
  private reconnectAttempts = 0;
  private reconnectBase = 2000;
  private reconnectMax = 30000;

  connect(userId: string) {
    this.userId = userId;
    if (this.isConnecting) return;
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.isConnecting = true;
    this.socket = new WebSocket(`${REACT_APP_WEBSOCKET_URL}`);
    const wasReconnect = this.reconnectAttempts > 0;
    this.socket.onopen = () => {
      this.isConnecting = false;
      console.log(
        wasReconnect
          ? `[WebSocket] Reconectado com sucesso (tentativa ${this.reconnectAttempts})`
          : '[WebSocket] Conectado com sucesso'
      );
      this.reconnectAttempts = 0;

      try {
        this.socket?.send(JSON.stringify({ userId: this.userId }));
      } catch (err) {
        console.error(err);
      }

      this.startHeartbeat();
    };

    this.socket.onmessage = (event) => {
      let data: WSData;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        console.warn('WS mensagem não-JSON:', event.data);
        return;
      }

      if (data?.type === 'pong') {
        this.lastPongAt = Date.now();
        return;
      }

      if (data?.eventType && data?.message && data?.userId) {
        this.listeners.forEach((cb) => cb(data));
      }
    };

    this.socket.onerror = (error) => {
      console.warn('WebSocket error', error);
    };

    this.socket.onclose = (ev) => {
      console.warn(
        `WebSocket onclose code=${ev.code} reason=${ev.reason || '(none)'} clean=${ev.wasClean}`
      );

      this.isConnecting = false;
      this.stopHeartbeat();

      this.socket = null;

      this.scheduleReconnect();
    };
  }

  private startHeartbeat() {
    this.stopHeartbeat();

    this.lastPongAt = Date.now();

    this.hbInterval = setInterval(() => {
      try {
        this.socket?.send(JSON.stringify({ type: 'ping', t: Date.now() }));
      } catch (err) {
        console.error(err);
      }
      this.restartHeartbeatWatchdog();
    }, 25000);

    this.restartHeartbeatWatchdog();
  }

  private restartHeartbeatWatchdog() {
    if (this.hbWatchdog) clearTimeout(this.hbWatchdog);

    this.hbWatchdog = setTimeout(() => {
      const age = Date.now() - this.lastPongAt;
      if (age > 20000) {
        console.warn('Heartbeat: sem PONG recente, reiniciando socket...');
        this.forceReconnect();
      }
    }, 21000);
  }

  private stopHeartbeat() {
    if (this.hbInterval) {
      clearInterval(this.hbInterval);
      this.hbInterval = null;
    }
    if (this.hbWatchdog) {
      clearTimeout(this.hbWatchdog);
      this.hbWatchdog = null;
    }
  }

  private scheduleReconnect() {
    if (!this.userId) return;
    if (this.reconnectTimeout) return;

    const jitter = Math.floor(Math.random() * 500);
    const delay =
      Math.min(
        this.reconnectBase * Math.pow(2, this.reconnectAttempts),
        this.reconnectMax
      ) + jitter;

    console.log(
      `Tentando reconectar em ${Math.round(delay / 1000)}s... (tentativa ${
        this.reconnectAttempts + 1
      })`
    );

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.reconnectAttempts += 1;
      this.connect(this.userId!);
    }, delay);
  }

  private forceReconnect() {
    try {
      this.socket?.close(4000, 'Heartbeat timeout');
    } catch (err) {
      console.error(err);
    }
  }

  addListener(cb: (data: WSData) => void) {
    this.listeners.push(cb);
  }

  removeListener(cb: (data: WSData) => void) {
    this.listeners = this.listeners.filter((x) => x !== cb);
  }

  isConnected(): boolean {
    return !!this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.stopHeartbeat();

    if (!this.socket) return;

    try {
      this.socket.onopen = null as any;
      this.socket.onmessage = null as any;
      this.socket.onerror = null as any;
      this.socket.onclose = null as any;

      if (
        this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING
      ) {
        this.socket.close(1000, 'Client disconnect');
      }
    } catch (_err) {
      console.error(_err);
    } finally {
      this.socket = null;
      this.isConnecting = false;
    }
  }
}

export const websocketService = new WebSocketService();
